import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { api } from '../lib/api'

export interface Document {
  id: string
  name: string
  pageCount: number
  uploadedAt: string
  status: 'processing' | 'ready' | 'error' | 'failed'
}

export function useDocuments() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isDeleting, setIsDeleting] = useState<string | null>(null)

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['documents'],
    queryFn: async () => {
      const token = await getToken()
      return api.getDocuments(token)
    },
    refetchInterval: (query) => {
      const docs = query.state.data as Document[] | undefined
      if (docs?.some((d) => d.status === 'processing')) return 3000
      return false
    },
  })

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const token = await getToken()
      return api.uploadDocument(file, token, (progress) => {
        setUploadProgress(progress)
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      setUploadProgress(0)
    },
    onError: () => {
      setUploadProgress(0)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (documentId: string) => {
      const token = await getToken()
      return api.deleteDocument(documentId, token)
    },
    onMutate: async (documentId: string) => {
      setIsDeleting(documentId)
      await queryClient.cancelQueries({ queryKey: ['documents'] })
      const previous = queryClient.getQueryData<Document[]>(['documents'])
      queryClient.setQueryData<Document[]>(['documents'], (old) =>
        old ? old.filter((d) => d.id !== documentId) : []
      )
      return { previous }
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['documents'], context.previous)
      }
      setIsDeleting(null)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      setIsDeleting(null)
    },
  })

  return {
    documents,
    isLoading,
    uploadDocument: uploadMutation.mutate,
    isUploading: uploadMutation.isPending,
    uploadProgress,
    deleteDocument: deleteMutation.mutate,
    isDeleting,
  }
}
